import { Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";

import { v2Route } from "~/v2/routes";

import { DbpjkitWorkerJobDetail } from "./DbpjkitWorkerJobDetail";

interface DbpjkitWorkerJobPeekProps {
  /** When set, the sheet is open and shows the detail of this job id. */
  id: string | null;
  onClose: () => void;
}

/**
 * Slide-in side panel driven by the `selectedDbpjkitWorkerJob` URL search param so
 * a refresh restores the panel. The maximize icon opens the full-page detail route.
 */
export function DbpjkitWorkerJobPeek({ id, onClose }: DbpjkitWorkerJobPeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = id !== null;

  const openFullPage = () => {
    if (!id) return;
    onClose();
    navigate(v2Route.dbpjkitWorkerJobs.detail.$buildPath({ params: { id } }));
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle>{t("model.dbpjkitWorkerJob.modelName")}</SheetTitle>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage", { defaultValue: "Open full page" })}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage", { defaultValue: "Open full page" })}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t("ui.close", { defaultValue: "Close" })}
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close", { defaultValue: "Close" })}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<div className="text-14-regular text-fg-3">…</div>}>
            {id !== null && <DbpjkitWorkerJobDetail id={id} onBack={onClose} variant="peek" />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
